import { z } from 'zod';

import { DateStringSchema } from '../../common';
import { makeEndpoint } from '../common';

/**
 * A price tier as it stood before and after a recommendation was applied, so
 * the record stands on its own even after prices move again.
 */
export const AppliedPricingRecommendationTierSchema = z.object({
  id: z.string(),
  name: z.string(),
  previousPrice: z.number(),
  appliedPrice: z.number(),
  /** Which limits bounded this tier; absent when no limits applied. */
  limitSource: z.enum(['rule', 'default']).optional(),
  floorUsed: z.number().optional(),
  ceilingUsed: z.number().optional(),
});
export type AppliedPricingRecommendationTier = z.infer<typeof AppliedPricingRecommendationTierSchema>;

/** Journal row identity: a hex digest of its key, served by GET pricing-recommendations as `id`. */
export const PricingRecommendationIdSchema = z.string().regex(/^[0-9a-f]{32}$/);

/**
 * What the API records when a brand applies a recommendation. Everything but
 * the actor comes from the journal row itself — the client only names the
 * recommendation; it cannot change what gets applied.
 */
export const AppliedPricingRecommendationSchema = z.object({
  id: z.string().uuid(),
  recommendationId: PricingRecommendationIdSchema,
  brandId: z.string().uuid(),
  entityType: z.enum(['experience', 'resource_group']),
  entityId: z.string().uuid(),
  sessionDate: DateStringSchema,
  sessionStart: z.string().regex(/^\d{2}:\d{2}$/),
  sessionDurationMinutes: z.number().int().nullable(),
  snapshotDate: DateStringSchema,
  /** `raise_10`, `lower_5`, … — the journaled action, as applied. */
  action: z.string(),
  compositeScore: z.number(),
  appliedById: z.string(),
  appliedAt: z.string(),
  tiers: z.array(AppliedPricingRecommendationTierSchema),
  /**
   * Who set the price: a person (`manual`), the engine's daily run (`dynamic-pricing`) or the engine reacting to a
   * recomputed recommendation (`dynamic-pricing-push`). Absent on records that predate it (manual).
   */
  trigger: z.enum(['dynamic-pricing', 'dynamic-pricing-push', 'manual']).optional(),
});
export type AppliedPricingRecommendation = z.infer<typeof AppliedPricingRecommendationSchema>;

/** The reasons the engine leaves a recommendation unapplied that say something about the listing, not about timing. */
export const SkippedPricingRecommendationReasonSchema = z.enum([
  'at_ceiling',
  'at_floor',
  'hotel_override',
  'cancellation_cannot_raise',
  'daily_only_lowers',
  'entity_unavailable',
  'experience_unavailable',
  'no_limits',
]);

/** What the engine records when it declines a recommendation, so the calendar can say why a price did not move. */
export const SkippedPricingRecommendationSchema = z.object({
  id: z.string().uuid(),
  recommendationId: PricingRecommendationIdSchema,
  brandId: z.string().uuid(),
  entityType: z.enum(['experience', 'resource_group']),
  entityId: z.string().uuid(),
  sessionDate: DateStringSchema,
  sessionStart: z.string().regex(/^\d{2}:\d{2}$/),
  sessionDurationMinutes: z.number().int().nullable(),
  snapshotDate: DateStringSchema,
  /** The recommended action that was not applied. */
  action: z.string(),
  trigger: z.enum(['dynamic-pricing', 'dynamic-pricing-push']),
  /** What set the engine off: the daily run, or a booking or a cancellation. */
  cause: z.enum(['daily', 'booking.created', 'booking.cancelled']),
  reason: SkippedPricingRecommendationReasonSchema,
  skippedAt: z.string(),
  /** Per tier where it explains the skip: what guests pay, what the action asked for, and the limits that held it. */
  tiers: z
    .array(
      z.object({
        name: z.string(),
        currentPrice: z.number(),
        wouldBePrice: z.number().optional(),
        floor: z.number().optional(),
        ceiling: z.number().optional(),
      }),
    )
    .optional(),
});
export type SkippedPricingRecommendation = z.infer<typeof SkippedPricingRecommendationSchema>;

export const ApplyPricingRecommendationEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, recommendationId }: { brandId: string; recommendationId: string }) =>
    `/v1/brands/${brandId}/pricing-recommendations/${recommendationId}/apply`,
  requestSchema: z.object({}),
  responseSchema: AppliedPricingRecommendationSchema,
});
